import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";
import { FigureOwnerType, Puppet, SessionType, isOpenToMany } from "../../domain/types";

type Participant = {
    display_name: string;
    participant_id?: string;
};

type Props = {
    participants: Participant[];
    isTherapist: boolean;
    sessionType: SessionType;
    onKick?: (participantId: string) => void;
    // Figures currently on the board, so each participant can be shown with the
    // figures they own (issue #197).
    puppets?: Puppet[];
    // Focus the board camera on a figure selected from the list.
    onFocusFigure?: (figureId: string) => void;
};

const CHIP_COLORS = [
    "bg-brand-100 text-brand-700",
    "bg-success-100 text-success-700",
    "bg-warning-100 text-warning-700",
    "bg-purple-100 text-purple-700",
];

function initials(name: string): string {
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? "")
        .join("");
}

function figuresOwnedBy(puppets: Puppet[], participantId: string | undefined): Puppet[] {
    if (!participantId) return [];
    return puppets.filter(
        (p) =>
            (p.ownerType ?? FigureOwnerType.THERAPIST) === FigureOwnerType.PARTICIPANT &&
            p.ownerId === participantId,
    );
}

function ParticipantRow({
    participant,
    colorClass,
    showKick,
    onKick,
    figures,
    onFocusFigure,
}: {
    participant: Participant;
    colorClass: string;
    showKick: boolean;
    onKick?: (participantId: string) => void;
    figures: Puppet[];
    onFocusFigure?: (figureId: string) => void;
}) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(true);
    const hasFigures = figures.length > 0;

    return (
        <li className="rounded-xl px-1 py-1">
            <div className="flex items-center gap-2">
                {hasFigures ? (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        aria-expanded={expanded}
                        aria-label={t("constellation.presence.toggleFigures", {
                            name: participant.display_name,
                        })}
                        className="flex h-5 w-5 items-center justify-center text-gray-500"
                    >
                        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                ) : (
                    <span className="h-5 w-5" />
                )}
                <div
                    className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${colorClass}`}
                >
                    {initials(participant.display_name)}
                    <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-success-500" />
                </div>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700 dark:text-gray-200">
                    {participant.display_name}
                </span>
                {hasFigures && (
                    <span className="shrink-0 rounded-full bg-gray-100 px-1.5 text-xs text-gray-500">
                        {figures.length}
                    </span>
                )}
                {showKick && participant.participant_id && (
                    <button
                        type="button"
                        onClick={() => onKick?.(participant.participant_id!)}
                        aria-label={t("constellation.presence.removeTooltip", {
                            name: participant.display_name,
                        })}
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs text-gray-600 hover:bg-red-100 hover:text-red-600"
                    >
                        ×
                    </button>
                )}
            </div>
            {expanded && hasFigures && (
                <ul className="ml-9 mt-1 flex flex-col gap-0.5">
                    {figures.map((f) => (
                        <li key={f.id}>
                            <button
                                type="button"
                                onClick={() => onFocusFigure?.(f.id)}
                                className="w-full truncate rounded px-2 py-1 text-left text-xs text-gray-600 hover:bg-gray-100 dark:text-gray-300"
                            >
                                {f.name || t("constellation.presence.unnamedFigure")}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
}

export default function PresencePanel({
    participants,
    isTherapist,
    sessionType,
    onKick,
    puppets = [],
    onFocusFigure,
}: Props) {
    const { t } = useTranslation();
    const showKick = isTherapist && isOpenToMany(sessionType);

    return (
        <div className="flex flex-col gap-2">
            <p className="text-xs text-gray-500">
                {t("constellation.presence.connected", { count: participants.length })}
            </p>
            <ul className="flex flex-col gap-1">
                {participants.map((p, i) => (
                    <ParticipantRow
                        key={p.participant_id ?? p.display_name}
                        participant={p}
                        colorClass={CHIP_COLORS[i % CHIP_COLORS.length]}
                        showKick={showKick}
                        onKick={onKick}
                        figures={figuresOwnedBy(puppets, p.participant_id)}
                        onFocusFigure={onFocusFigure}
                    />
                ))}
            </ul>
        </div>
    );
}
